import type { FocusEvent } from 'react'

import { formatIntegerInput } from './numericInput'

/** Bo'lim komponentlariga (`PayoutSection.tsx` va h.k.) uzatiladigan bitta maydon holati. */
export interface FieldBinding {
  value: string
  onChange: (value: string) => void
  error: string | null
}

export interface NumberFieldProps {
  id: string
  /** `docs/03-app-flow.md` maketidagi qator nomi, masalan "1000 prosmotr uchun". */
  label: string
  /** Qiymatdan keyin ko'rsatiladigan birlik, masalan "so'm" / "kun" / "-sana". */
  unit?: string
  value: string
  onChange: (value: string) => void
  error: string | null
  disabled?: boolean
  placeholder?: string
}

/**
 * Sozlamalar sahifasidagi har bir raqamli qator — `components/videos/VideoChecksSection.tsx`
 * dagi `ViewsInput` bilan AYNAN BIR XIL naqsh (vazifa talabi — "MAJBURIY"):
 *
 * - `type="text"` + `inputMode="numeric"` — telefonda raqamli klaviatura, lekin brauzer
 *   bo'shliq/vergul kabi ming ajratkichlarini kiritishga to'sqinlik qilmaydi
 *   (`type="number"` bunday qiymatni rad etadi yoki jimgina bo'shatib qo'yadi).
 * - `text-base` (16px) — bundan kichik shrift iOS Safari'da fokusda avtomatik zoom
 *   qiladi, natijada pastdagi "Saqlash" tugmasi ekrandan chiqib ketishi mumkin.
 * - Fokusda `scrollIntoView({ block: 'center' })`, kichik kechikish bilan — mobil
 *   klaviatura animatsiyasi tugagunча kutiladi, aks holda hisoblangan pozitsiya hali
 *   klaviatura balandligini hisobga olmagan bo'ladi.
 */
export function NumberField({
  id,
  label,
  unit,
  value,
  onChange,
  error,
  disabled,
  placeholder,
}: NumberFieldProps) {
  function handleFocus(event: FocusEvent<HTMLInputElement>) {
    const el = event.currentTarget
    window.setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300)
  }

  return (
    <div className="flex flex-col gap-1 py-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label htmlFor={id} className="text-sm text-app-hint">
          {label}
        </label>
        <div className="flex items-center gap-2">
          <input
            id={id}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={value}
            // Har o'zgarishda ming ajratkichi bilan qayta formatlanadi ("100000" → "100 000")
            // — o'ng tomonga tekislangan raqamli maydon, kursor odatda oxirida bo'ladi.
            onChange={(event) => onChange(formatIntegerInput(event.target.value))}
            onFocus={handleFocus}
            disabled={disabled}
            placeholder={placeholder}
            aria-invalid={error !== null}
            className="tabular h-11 w-28 rounded-input border border-app-border bg-app-bg px-3 text-right text-base text-app-text outline-none focus-visible:border-brand disabled:opacity-60"
          />
          {unit && <span className="shrink-0 text-sm text-app-hint">{unit}</span>}
        </div>
      </div>
      {error && <p className="text-right text-xs text-stage-rejected-text">{error}</p>}
    </div>
  )
}
